import { useQuery, useMutation, useInfiniteQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { reportsApi } from '../../services/api'
import type {
  ExecutivePosturePayload,
  AssetDeepDivePayload,
} from '../../services/api/reports.api'
import { useAuthStore } from '../../store/authStore'
import type {
  ReportHistoryResults,
  ReportHistory,
  ReportKind,
  ReportListFilters,
  ReportPatch,
  ScanReportData,
  ReportTemplateResults,
  ReportTemplate,
  BodyData
} from '../../app/modules/types/report'
import { toast } from 'sonner'

/**
 * Hook for fetching paginated report history
 * Automatically refetches when URL params change
 */
export function useReportHistory() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.reports.history(orgId, params),
    queryFn: () => reportsApi.getList(params),
    enabled: !!orgId,
  })
}

/** Serialise the history filters into the list endpoint's query string. */
export function buildReportListQuery(filters: ReportListFilters = {}, page?: number): string {
  const usp = new URLSearchParams()
  if (page) usp.set('page', String(page))
  const search = filters.search?.trim()
  if (search) usp.set('search', search)
  if (filters.kind?.length) usp.set('kind', filters.kind.join(','))
  if (filters.status) usp.set('status', filters.status)
  if (filters.file_type) usp.set('file_type', filters.file_type)
  if (filters.lang) usp.set('lang', filters.lang)
  if (filters.tags?.length) usp.set('tags', filters.tags.join(','))
  if (filters.created_by) usp.set('created_by', filters.created_by)
  if (filters.pinned !== undefined) usp.set('pinned', String(filters.pinned))
  if (filters.created_after) usp.set('created_after', filters.created_after)
  if (filters.created_before) usp.set('created_before', filters.created_before)
  if (filters.ordering) usp.set('ordering', filters.ordering)
  return usp.toString()
}

/**
 * Hook for the incrementally loaded report history. The list is capped at 10
 * rows a page and carries no total count — only `total_pages`.
 */
export function useReportHistoryInfinite(filters: ReportListFilters = {}) {
  const orgId = useAuthStore((state) => state.orgId)
  const key = buildReportListQuery(filters)
  return useInfiniteQuery({
    queryKey: queryKeys.reports.historyInfinite(orgId, key),
    queryFn: ({ pageParam = 1 }): Promise<ReportHistoryResults> =>
      reportsApi.getList(buildReportListQuery(filters, pageParam)),
    getNextPageParam: (lastPage) => {
      if (lastPage.current_page < lastPage.total_pages) {
        return lastPage.current_page + 1
      }
      return undefined
    },
    initialPageParam: 1,
    enabled: !!orgId,
  })
}

/** Distinct tags of the organization starting with `search` — the tag autocomplete. */
export function useReportTags(search = '', enabled = true) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.reports.tags(orgId, search),
    queryFn: () => reportsApi.getTags(search || undefined),
    enabled: !!orgId && enabled,
    staleTime: 30_000,
  })
}

/**
 * The name a launch with this body would get, for the create form's placeholder.
 * Keeps the previous placeholder while a new one loads and when the body is not
 * (yet) valid — a 400/403/404 never clears it.
 */
export function useReportDefaultName(
  kind: ReportKind,
  body: Record<string, unknown>,
  enabled: boolean
) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.reports.defaultName(orgId, `${kind}:${JSON.stringify(body)}`),
    queryFn: () => reportsApi.getDefaultName(kind, body),
    enabled: !!orgId && enabled,
    placeholderData: (previous) => previous,
    retry: false,
    staleTime: 60_000,
  })
}

/** Edit name / description / tags. The server's response is the truth (tags come back lowercased). */
export function useUpdateReport() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ReportPatch }) =>
      reportsApi.update(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all(orgId) })
    },
  })
}

/** Pin or unpin for the current user. */
export function usePinReport() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      pinned ? reportsApi.pin(id) : reportsApi.unpin(id),
    onSuccess: () => {
      // Pinned rows sort first, so a toggle reorders the list: refetch, don't patch.
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all(orgId) })
    },
  })
}

/**
 * Hook for fetching a single report by ID
 */
export function useReportDetail(id: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.reports.detail(orgId, id || ''),
    queryFn: () => reportsApi.getById(id!),
    enabled: !!id && !!orgId,
  })
}
export function useReportDownload() {
  return useMutation({
    mutationFn: ({ id }: { id: string; name: string }) => reportsApi.download(id),
    onSuccess: (data, { name }) => {
      const url = data.download_url;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', data.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  })
}

/**
 * Hook for fetching scan reports table (scans with vulnerability filters)
 */
export function useScanReportsTable() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.reports.scanReports(orgId, params),
    queryFn: () => reportsApi.getScanReportsTable(params),
    enabled: !!orgId,
  })
}

/**
 * Hook for infinite scroll scan reports table
 */
export function useScanReportsTableInfinite() {
  const orgId = useAuthStore((state) => state.orgId)
  return useInfiniteQuery({
    queryKey: queryKeys.reports.scanReportsInfinite(orgId),
    queryFn: ({ pageParam = 1 }): Promise<ScanReportData> => {
      return reportsApi.getScanReportsTable(`page=${pageParam}`)
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.current_page < lastPage.total_pages) {
        return lastPage.current_page + 1
      }
      return undefined
    },
    initialPageParam: 1,
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching report templates
 */
export function useReportTemplates() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.reports.templates(orgId, params),
    queryFn: () => reportsApi.getTemplates(params),
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching a single report template by ID
 */
export function useReportTemplateDetail(id: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.reports.templateDetail(orgId, id || ''),
    queryFn: () => reportsApi.getTemplateById(id!),
    enabled: !!id && !!orgId,
  })
}

/**
 * Hook for creating a new report (single scan or multi-scan)
 */
export function useCreateScanReport() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({
      data,
      isSingleScan
    }: {
      data: BodyData
      isSingleScan: boolean
    }) => reportsApi.createReport(data, isSingleScan),
    onSuccess: () => {
      // Invalidate report history queries to refetch the updated list
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all(orgId) })
    },
  })
}

/**
 * Hook for creating an executive posture report (org-wide, period-based)
 */
export function useCreateExecutivePostureReport() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (payload: ExecutivePosturePayload) =>
      reportsApi.createExecutivePostureReport(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all(orgId) })
    },
  })
}

/**
 * Hook for creating an asset deep-dive report (single host or IP)
 */
export function useCreateAssetDeepDiveReport() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (payload: AssetDeepDivePayload) =>
      reportsApi.createAssetDeepDiveReport(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all(orgId) })
    },
  })
}

/**
 * Hook for deleting a report
 */
export function useDeleteReport() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (id: string) => reportsApi.delete(id),
    onSuccess: () => {
      // Invalidate report history queries to refetch the updated list
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all(orgId) })
    },
  })
}

/**
 * Hook for creating a new report template
 */
export function useCreateReportTemplate() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (data: Partial<ReportTemplate>) => reportsApi.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.templatesAll(orgId) })
    },
  })
}

/**
 * Hook for updating a report template
 */
export function useUpdateReportTemplate() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ReportTemplate> }) =>
      reportsApi.updateTemplate(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.templatesAll(orgId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.templateDetail(orgId, variables.id) })
    },
  })
}

/**
 * Hook for deleting a report template
 */
export function useDeleteReportTemplate() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (id: string) => reportsApi.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.templatesAll(orgId) })
    },
  })
}

/**
 * Helper hook for polling pending reports
 * Polling interval controlled via environment variables
 */
export function useReportHistoryWithPolling() {
  const orgId = useAuthStore((state) => state.orgId)
  const { data, ...rest } = useReportHistory()

  // Check if there are any pending reports
  const hasPendingReports = data?.results?.some((report) => report.status === 'PENDING')

  return useQuery({
    ...rest,
    queryKey: queryKeys.reports.history(orgId, ''),
    queryFn: () => reportsApi.getList(),
    refetchInterval: hasPendingReports ? QUERY_CONFIG.REPORTS_PENDING.refetchInterval : false,
  })
}
